import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';

import styles from './EditUser.module.scss';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { UserDetailsResponseModel } from '../../../models/users/UserDetailsResponseModel';
import { nameof } from '../../../shared/utility';
import { Routes } from '../../../routes';
import { UserRequestModel } from '../../../models/users/UserRequestModel';

const EditUser = (): JSX.Element => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const { id } = useParams<{ id: string }>();

	const navigate = useNavigate();
	const location = useLocation();

	const currentPage = location.state?.currentPage;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [shouldReload, setShouldReload] = useState<boolean>(false);

	const [userData, setUserData] = useState<UserRequestModel>({
		firstName: '',
		lastName: '',
		userName: '',
		email: '',
		securityRoleId: undefined,
		defaultDivisionId: undefined,
		divisions: []
	});

	const {
		firstName,
		lastName,
		userName,
		email,
		securityRoleId,
		defaultDivisionId,
		divisions
	} = userData;

	const [activeDivisionsIds, setActiveDivisionsIds] = useState<number[]>([]);

	const [securityRolesOptions, setSecurityRolesOptions] = useState<
		DropdownOption[]
	>([]);

	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>(
		[]
	);

	const fetchUser = useCallback(() => {
		httpService
			.get<UserDetailsResponseModel>(`users/${id}`, undefined)
			.then((user) => {
				setUserData({
					firstName: user.firstname,
					lastName: user.lastname,
					userName: user.username,
					email: user.email,
					securityRoleId: user.securityRoleId,
					defaultDivisionId: user.defaultDivisionId,
					divisions: user.divisions
				});

				setActiveDivisionsIds(user.divisions.map((d) => d.divisionId));
			})
			.catch((err) => notifierService.error());
	}, [id, httpService, notifierService]);

	const fetchSecurityRoles = useCallback(() => {
		httpService
			.get<DropdownOption[]>('securityRoles/dropdown', undefined)
			.then((options) => setSecurityRolesOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	const fetchDivisions = useCallback(() => {
		httpService
			.get<DropdownOption[]>('divisions/dropdown', undefined)
			.then((options) => setDivisionsOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	useEffect(() => {
		fetchUser();
		fetchSecurityRoles();
		fetchDivisions();
	}, [
		id,
		httpService,
		notifierService,
		fetchUser,
		fetchSecurityRoles,
		fetchDivisions
	]);

	useEffect(() => {
		if (shouldReload) {
			fetchUser();
			setShouldReload(false);
		}
	}, [shouldReload, fetchUser]);

	const navigateBack = () => {
		navigate(Routes.users.getPath(), {
			state: { currentPage: currentPage }
		});
	};

	const handleEditUser = async (): Promise<void> => {
		if (
			!firstName ||
			!lastName ||
			!userName ||
			!email ||
			!securityRoleId ||
			!defaultDivisionId ||
			!divisions
		) {
			return;
		}

		setIsLoading(true);

		try {
			await httpService.put(
				`users/${id}`,
				JSON.stringify(userData),
				undefined,
				false
			);

			setShouldReload(true);

			notifierService.info(`User '${userData.userName}' was edited`);

			navigateBack();
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const onReceiveNotificationChange = (e: {
		value: number;
		checked: boolean;
	}) => {
		const newDivisionNotifications = [...divisions];
		const index = newDivisionNotifications.findIndex(
			(n) => n.divisionId === e.value
		);

		const divisionId = e.value;
		const receiveDivisionalNotifications = e.checked;

		if (index === -1) {
			newDivisionNotifications.push({
				divisionId,
				receiveDivisionalNotifications
			});
		} else {
			newDivisionNotifications[index].receiveDivisionalNotifications =
				receiveDivisionalNotifications;
		}

		setUserData({
			...userData,
			divisions: newDivisionNotifications
		});
	};

	const onActiveDivisionsChange = (e: MultiSelectChangeParams) => {
		const divisionsIds: number[] = e.target.value;
		setActiveDivisionsIds(divisionsIds);

		const filteredNotifications = [...divisions].filter((n) =>
			divisionsIds.some((i) => i === n.divisionId)
		);

		divisionsIds.forEach((divisionId) => {
			const receiveDivisionalNotifications =
				divisions.find((n) => n.divisionId === divisionId)
					?.receiveDivisionalNotifications ?? false;

			const index = filteredNotifications.findIndex(
				(n) => n.divisionId === divisionId
			);

			if (index === -1) {
				filteredNotifications.push({
					divisionId,
					receiveDivisionalNotifications
				});
			}

			setUserData({
				...userData,
				divisions: filteredNotifications
			});
		});
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.inputsContainer}>
					<div className={styles.content}>
						<label htmlFor='firstName'>First name</label>
						<InputText
							id='firstName'
							placeholder='Firstname'
							value={firstName}
							onChange={(e) => {
								setUserData({
									...userData,
									firstName: e.target.value
								});
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='lastName'>Last name</label>
						<InputText
							id='lastName'
							placeholder='Lastname'
							value={lastName}
							onChange={(e) => {
								setUserData({
									...userData,
									lastName: e.target.value
								});
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='userName'>Username</label>
						<InputText
							id='userName'
							placeholder='Username'
							value={userName}
							onChange={(e) => {
								setUserData({
									...userData,
									userName: e.target.value
								});
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='email'>Email</label>
						<InputText
							id='email'
							placeholder='Email'
							value={email}
							onChange={(e) => {
								setUserData({
									...userData,
									email: e.target.value
								});
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='securityRole'>Security role</label>
						<Dropdown
							id='securityRole'
							value={securityRoleId}
							options={securityRolesOptions}
							onChange={(e) => {
								setUserData({
									...userData,
									securityRoleId: e.target.value
								});
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='defaultDivision'>
							Default division
						</label>
						<Dropdown
							id='defaultDivision'
							value={defaultDivisionId}
							options={divisionsOptions}
							onChange={(e) => {
								setUserData({
									...userData,
									defaultDivisionId: e.target.value
								});
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='activeDivisions'>
							Active divisions
						</label>
						<MultiSelect
							id='activeDivisions'
							placeholder='Select active divisions'
							value={activeDivisionsIds}
							options={divisionsOptions}
							optionLabel={nameof<DropdownOption>('label')}
							optionValue={nameof<DropdownOption>('value')}
							onChange={onActiveDivisionsChange}
						/>
					</div>

					{activeDivisionsIds.length > 0 && divisions ? (
						<div className={styles.content}>
							<label htmlFor='notifications'>
								Receive notifications
							</label>
							{activeDivisionsIds.map((divisionId) => {
								return (
									<div
										key={divisionId}
										className={styles.fieldCheckbox}
									>
										<Checkbox
											inputId={divisionId.toString()}
											value={divisionId}
											checked={
												divisions.find(
													(n) =>
														n.divisionId ===
														divisionId
												)
													?.receiveDivisionalNotifications
											}
											onChange={
												onReceiveNotificationChange
											}
										/>
										<label htmlFor={divisionId.toString()}>
											{
												divisionsOptions.find(
													(d) =>
														d.value === divisionId
												)?.label
											}
										</label>
									</div>
								);
							})}
						</div>
					) : (
						<br />
					)}
				</div>

				<div className={styles.footer}>
					<button
						className='btn primary'
						disabled={isLoading}
						onClick={handleEditUser}
					>
						Save
					</button>
					<button className='btn secondary' onClick={navigateBack}>
						Cancel
					</button>
				</div>
			</div>
		</>
	);
};

export default EditUser;
